import { modelManager } from '@/editor/manager/entityManager';
import { TGlobalStores } from '@/editor/stores';
import { EEntityType } from '@/editor/types';
import { EVisibleEntityType, TVisibleEntityKey } from '@/editor/types/visible';
import { getShouldUpdateIds, getNewHiddenEntitiesIds } from './utils/getNewHiddenEntitiesIds';
import { getAllEntityIdsByType } from './utils/getEntityIdsByType';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { intersection } from 'lodash-es';

/** 多选里可能出现的成员类型：框选得到家具/组合/隔墙，「选同款」得到门窗 */
const MULTI_MODEL_MEMBER_TYPES: EEntityType[] = [
  EEntityType.Furniture,
  EEntityType.Assembly,
  EEntityType.SeparateWall,
  EEntityType.DoorWindow
];

export class VisibleOperation {
  private _gs: TGlobalStores;
  private _editorStateStore: TGlobalStores['editorStateStore'];
  private _visibilityStore: TGlobalStores['visibilityStore'];
  private _faceStore: TGlobalStores['faceStore'];
  private _roomStore: TGlobalStores['roomStore'];

  constructor(gs: TGlobalStores) {
    this._editorStateStore = gs.editorStateStore;
    this._visibilityStore = gs.visibilityStore;
    this._faceStore = gs.faceStore;
    this._roomStore = gs.roomStore;
    this._gs = gs;
  }

  /**
   * 获取当前应当操作的构件ID
   * @param type 构件类型
   * @param v 是否可见
   * @param targetIds 需要操作的构件ID，如果为空则为同类型下所有构件
   * @returns
   */
  private _getUpdateIds(type: EEntityType, v: boolean, targetIds?: string[]) {
    const shadowTargetList = targetIds?.length
      ? targetIds
      : v
        ? Array.from(this._visibilityStore.getHiddenEntitiesIdsByEntityType(type))
        : undefined;
    // 隐藏时快照当前全部 id：显隐只影响操作当时已存在的构件，之后新绘制的构件正常显示
    const list = shadowTargetList ?? getAllEntityIdsByType(this._gs, type);

    if (list.length === 0) return [];
    const updateIds = getShouldUpdateIds(type, v, list);
    return updateIds;
  }

  private _updateHiddenEntitiesList(type: EEntityType, v: boolean, ids: string[]) {
    const newList = getNewHiddenEntitiesIds(type, v, ids);
    this._visibilityStore.setEntityTypeHiddenIds(type, newList);
  }

  /** 检查当前选中构建是否在隐藏构件队列中 */
  private _checkSelectedEntityIsHidden(type: EEntityType | EEntityType[], hiddenIds: string[]) {
    const paramsType = Array.isArray(type) ? type : [type];
    const selectedType = this._editorStateStore.selected.type;
    const selectedId = this._editorStateStore.selected.entityId;
    switch (selectedType) {
      case EEntityType.EMPTY: {
        return false;
      }
      case EEntityType.Face: {
        const { relativeInfo } = this._faceStore.getFaceData(selectedId) ?? {};
        const relativeEntityId = relativeInfo?.wallId || relativeInfo?.sepId || relativeInfo?.columnId || '';
        return relativeEntityId && hiddenIds.includes(relativeEntityId);
      }
      case EEntityType.MultiModel: {
        // 这次隐藏的类型有没有可能出现在多选里，没有才短路掉后面的回查。
        // 漏掉隔墙会让「多选含隔墙 → 隐藏边界/隔墙」不取消选中，与隐藏家具的表现不一致
        const maybeMultiModel =
          paramsType.includes(EEntityType.MultiModel) ||
          paramsType.some((paramType) => MULTI_MODEL_MEMBER_TYPES.includes(paramType));
        if (!maybeMultiModel) return false;
        const multiData = this._gs.getDataByIdType('', EEntityType.MultiModel);
        if (!multiData) return false;
        const multiModelIds = multiData.modelInfos.reduce<string[]>((acc, cur) => {
          if (cur.type === EEntityType.Assembly) {
            acc.push(...cur.subModeldatas.map((item) => item.id));
          } else {
            acc.push(cur.id);
          }
          return acc;
        }, []);
        // 检查多选包含的子 id 是否在隐藏 id 列表里
        // TODO: 这部分后续可以考虑加类型判定加上，现在只判断 id （id 的碰撞概率极低）
        return intersection(hiddenIds, multiModelIds).length > 0;
      }
      default: {
        const isSameType = paramsType.includes(selectedType);
        return isSameType && hiddenIds.includes(selectedId);
      }
    }
  }

  private _setWallAndColumnVisibleByDesignatedIds(v: boolean, wallIds?: string[], columnIds?: string[]) {
    if (wallIds?.length) {
      this._updateHiddenEntitiesList(EEntityType.Wall, v, wallIds);
    }
    if (columnIds?.length) {
      this._updateHiddenEntitiesList(EEntityType.Column, v, columnIds);
    }

    modelManager.updateEntity();
  }

  setSingleEntityTypeVisible(type: TVisibleEntityKey, v: boolean, targetIds?: string[]) {
    const updateIds = this._getUpdateIds(type, v, targetIds);
    this._visibilityStore.setVisibleState(type, v);
    this._updateHiddenEntitiesList(type, v, updateIds);
    if (!v && this._checkSelectedEntityIsHidden(type, updateIds)) {
      this._editorStateStore.clearSelected();
    }

    modelManager.updateEntity();
  }

  setFurnitureVisible(v: boolean, targetIds?: string[]) {
    const furnitureUpdateIds = this._getUpdateIds(EEntityType.Furniture, v, targetIds);
    const assemblyUpdateIds = this._getUpdateIds(EEntityType.Assembly, v);
    // Assembly 没有独立的开关，它跟着 Furniture 一起走，只维护隐藏集不写 visibleState
    this._visibilityStore.setVisibleState(EEntityType.Furniture, v);
    this._updateHiddenEntitiesList(EEntityType.Furniture, v, furnitureUpdateIds);
    this._updateHiddenEntitiesList(EEntityType.Assembly, v, assemblyUpdateIds);
    if (
      !v &&
      this._checkSelectedEntityIsHidden(
        [EEntityType.Furniture, EEntityType.Assembly],
        [...furnitureUpdateIds, ...assemblyUpdateIds]
      )
    ) {
      this._editorStateStore.clearSelected();
    }

    modelManager.updateEntity();
  }

  setColumnAndWallEntitiesVisible(v: boolean) {
    const wallIds = this._getUpdateIds(EEntityType.Wall, v);
    const columnIds = this._getUpdateIds(EEntityType.Column, v);
    this._visibilityStore.setVisibleState(EVisibleEntityType.ColumnAndWall, v);
    this._setWallAndColumnVisibleByDesignatedIds(v, wallIds, columnIds);
    if (wallIds.length || columnIds.length) {
      if (!v && this._checkSelectedEntityIsHidden([EEntityType.Wall, EEntityType.Column], [...wallIds, ...columnIds])) {
        this._editorStateStore.clearSelected();
      }
    }
  }

  /** 底图显隐的唯一写入方：开关落 store，再让 manager 施加到 entity */
  setFloorPlanVisible(v: boolean) {
    this._visibilityStore.setVisibleState(EEntityType.FloorPlan, v);
    modelManager.floorPlanManager.applyVisible(v);
  }

  setRulerVisible(v: boolean) {
    this._visibilityStore.setVisibleState(EVisibleEntityType.Ruler, v);
    editorEvent.emit(EditorEventName.REFRESH_RULER);

    // 隐藏标尺时同时隐藏手绘的尺寸标注。
    // 注意这两者语义不同：墙体标尺由上面的开关全局控制、新画的墙自动继承；
    // 手绘尺寸标注走快照式隐藏集，之后新画的仍会显示，「尺寸线」的部分隐藏态就来自这里
    const linearDimensionUpdateIds = this._getUpdateIds(EEntityType.LinearDimension, v);
    this._updateHiddenEntitiesList(EEntityType.LinearDimension, v, linearDimensionUpdateIds);
    modelManager.dimensionManager.update();
  }

  setRegionTextInfoVisible(v: boolean) {
    this._visibilityStore.setVisibleState(EVisibleEntityType.RegionTextInfo, v);
    modelManager.textInfoManager.setRegionTextInfoVisible(v);
  }

  setRoomTextInfoVisible(v: boolean) {
    this._visibilityStore.setVisibleState(EVisibleEntityType.RoomTextInfo, v);
    modelManager.textInfoManager.setRoomTextInfoVisible(v);
  }

  /**
   * 恢复全部显隐：清空所有隐藏集 + 把所有开关复位为可见
   *
   * 直接清空 `_hiddenEntitiesList` 而不是遍历 `visibleState` 逐类恢复：
   * 1. Assembly / LinearDimension 有隐藏集却没有独立的 visibleState 键，按开关遍历会漏掉它们
   * 2. 快照式类型处于「部分隐藏」时开关布尔可能仍是 true，按开关遍历会整段跳过
   */
  showAll() {
    const hiddenCleared = this._visibilityStore.clearAllHiddenEntities();
    this._visibilityStore.resetVisibleState();

    // 继承式类型的可见性不落在隐藏集里，得显式驱动各自的 manager
    modelManager.textInfoManager.setRoomTextInfoVisible(true);
    modelManager.textInfoManager.setRegionTextInfoVisible(true);
    modelManager.floorPlanManager.applyVisible(true);
    editorEvent.emit(EditorEventName.REFRESH_RULER);

    modelManager.updateEntity();
    if (hiddenCleared) modelManager.textInfoManager.updateAreaDimensionTextInfos();
  }

  setWorkPointsVisible(targetIds: string[]) {
    modelManager.furnitureManager.showWorkPoints(targetIds);
  }

  setAreaDimensionVisible(visible: boolean, targetIds?: string[]) {
    this._visibilityStore.setVisibleState(EEntityType.AreaDimension, visible);
    const areaDimensionUpdateIds = this._getUpdateIds(EEntityType.AreaDimension, visible, targetIds);
    this._updateHiddenEntitiesList(EEntityType.AreaDimension, visible, areaDimensionUpdateIds);

    modelManager.dimensionManager.update();
    modelManager.textInfoManager.updateAreaDimensionTextInfos();
  }

  toggleRoomHighlight(roomId?: string) {
    if (!roomId) {
      modelManager.roomHighlightManager.clear();
    } else {
      const roomData = this._roomStore.getRoomData(roomId);
      if (roomData) modelManager.roomHighlightManager.update(roomData);
    }
  }
}
